import { api } from "@/lib/api";
import {
  FileText, FileSpreadsheet, FileImage, File as FileIcon, Mail as MailIcon,
} from "lucide-react";

// How long to keep an object URL alive after opening a preview in a new tab.
const PREVIEW_REVOKE_MS = 60000;
// Short delay before revoking a download object URL (lets the click finish).
const DOWNLOAD_REVOKE_MS = 2000;

// Business document categories (must match backend files.py DOC_TYPES).
export const DEFAULT_DOC_TYPE = "uncategorized";
export const DOC_TYPES = [
  { value: "uncategorized", label: "Uncategorized", color: "text-gray-600 bg-gray-100" },
  { value: "email", label: "Email", color: "text-amber-700 bg-amber-50" },
  { value: "invoice", label: "Invoice", color: "text-green-700 bg-green-50" },
  { value: "payment_request", label: "Request for Payment", color: "text-orange-700 bg-orange-50" },
  { value: "formal_document", label: "Formal Document", color: "text-blue-700 bg-blue-50" },
  { value: "regular_correspondence", label: "Regular Correspondence", color: "text-indigo-700 bg-indigo-50" },
  { value: "certified_mail_sent", label: "Certified Mail Sent", color: "text-rose-700 bg-rose-50" },
  { value: "cost_invoice", label: "Cost Invoices", color: "text-teal-700 bg-teal-50" },
];
const DOC_TYPE_MAP = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t]));
export const docTypeLabel = (v) => (DOC_TYPE_MAP[v] || DOC_TYPE_MAP[DEFAULT_DOC_TYPE]).label;
export const docTypeColor = (v) => (DOC_TYPE_MAP[v] || DOC_TYPE_MAP[DEFAULT_DOC_TYPE]).color;

export function fileCategory(name = "", ct = "") {
  const ext = name.split(".").pop().toLowerCase();
  if (ct.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) return "image";
  if (["xls", "xlsx", "csv"].includes(ext)) return "spreadsheet";
  if (["doc", "docx", "rtf", "odt"].includes(ext)) return "document";
  if (["msg", "eml"].includes(ext)) return "email";
  if (ext === "pdf") return "pdf";
  return "other";
}

export function fileIconFor(name = "", ct = "") {
  switch (fileCategory(name, ct)) {
    case "image": return { Icon: FileImage, color: "text-purple-600 bg-purple-50" };
    case "spreadsheet": return { Icon: FileSpreadsheet, color: "text-green-600 bg-green-50" };
    case "document": return { Icon: FileText, color: "text-blue-600 bg-blue-50" };
    case "email": return { Icon: MailIcon, color: "text-amber-600 bg-amber-50" };
    case "pdf": return { Icon: FileText, color: "text-red-600 bg-red-50" };
    default: return { Icon: FileIcon, color: "text-gray-500 bg-gray-100" };
  }
}

async function fetchBlob(fileId) {
  const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
  return res.data;
}

// Save the file to the local computer with its original name so it opens in the OS default app.
export async function downloadFile(fileId, filename) {
  const blob = await fetchBlob(fileId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "file";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_MS);
}

// Open in a new tab (browser previews viewable types; others are handed to the OS default app).
export async function openFile(fileId, filename) {
  const blob = await fetchBlob(fileId);
  const cat = fileCategory(filename, blob.type);
  const url = URL.createObjectURL(blob);
  if (cat === "image" || cat === "pdf") {
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), PREVIEW_REVOKE_MS);
  } else {
    // Not browser-previewable → download so it opens in the local default application.
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_MS);
  }
}
